import { useQuery } from "@apollo/client";
import { getBexApiContext } from "bernie-context";
import { ExtendedContextStore } from "src/typings/flexFramework/FlexDefinitions";
import { getPropertyTypeCode } from "src/components/utility/ContextUtil";
import {
  PropertyFiltersQueryDocument,
  PropertyFiltersQueryQueryVariables,
  SelectedValueInput,
} from "__generated__/typedefs";

function getStructureTypes(context: ExtendedContextStore) {
  const code = getPropertyTypeCode(context);

  const toDashCase = (key: string) => key.replace(/[A-Z]/g, (l) => `_${l.toLowerCase()}`);

  const structureTypes = code?.split(",").map((type) => toDashCase(type).toUpperCase());

  if (!structureTypes) {
    return null;
  }

  switch (true) {
    case structureTypes[0] === "BED_BREAKFAST":
      structureTypes[0] = "BED_AND_BREAKFAST";
      break;
    case structureTypes[0] === "_C_A_P_S_U_L_E__H_O_T_E_L":
      structureTypes[0] = "CAPSULE_HOTEL";
      break;
    case structureTypes[0] === "RYOKEN":
      structureTypes[0] = "RYOKAN";
      break;
  }

  return structureTypes[0];
}

function getLocation(context: ExtendedContextStore) {
  const location = context.searchContext.location;
  const { latitude, longitude } = location.geoCoordinate || {};
  if (location.type === "point_of_interest") {
    return {
      poi: `${latitude},${longitude}:${location.id}`,
    };
  }

  return {
    regionId: `${latitude},${longitude}:${location.id}`,
  };
}

export const generateSelectionsInput = (context: ExtendedContextStore): SelectedValueInput[] => {
  const { sort, paymentType, propertyStyle, travelerType, amenities } =
    context.searchContext.affinity?.bexApiFilters || {};

  const starRating = context.searchContext.starRating?.hsrFilterCode
    ? Number(context.searchContext.starRating.hsrFilterCode)
    : undefined;

  const structureTypes = getStructureTypes(context);
  const { regionId, poi } = getLocation(context);

  const selections = {
    sort,
    paymentType,
    propertyStyle,
    travelerType,
    amenities,
    starRating,
    structureTypes,
    regionId,
    poi,
  };

  return Object.keys(selections)
    .filter((s) => selections[s])
    .map((s) => ({ id: s, value: selections[s] }));
};

export function usePropertySearchQuery(context: ExtendedContextStore) {
  const destination = { regionId: String(context?.searchContext?.location?.id) };
  const selections = generateSelectionsInput(context);

  const variables: PropertyFiltersQueryQueryVariables = {
    context: getBexApiContext(context),
    destination: destination,
    criteria: {
      primary: {
        destination: destination,
        dateRange: null,
        rooms: [],
      },
      secondary: {
        selections,
      },
    },
  };

  return useQuery(PropertyFiltersQueryDocument, {
    variables,
    errorPolicy: "all",
    fetchPolicy: "cache-first",
  });
}

export default usePropertySearchQuery;
